import { Trash2 } from 'lucide-react'
import { Row } from 'src/shared/components/Semantic/all'
import { TableCell } from 'src/shared/components/ui/table'
import type { ProductReceiptLine } from 'src/shared/components/OrderTable/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { ProductRowPriceSummary } from '../ProductRowPriceSummary'
import { ProductRowQuantityControl } from './ProductRowQuantityCell'

export const ProductRowTotalCell = ({
  discount,
  subtotalBeforeDiscount,
  total,
  showDelete,
  onDeleteClick,
  priceSummaryLines,
  lineTotal,
  isEditMode = false,
  productUnitPrice,
  onProductUnitPriceChange,
  onDiscountChange,
  onDeleteSubItem,
  onEditDecoration,
  onChangeDecoration,
  onAddDecoration,
  onAddCustomLine,
  canAddCustomLine = false,
  quantity,
  onQuantityChange,
  isReadOnly = false,
}: {
  discount: number
  subtotalBeforeDiscount: number
  total: number
  showDelete: boolean
  onDeleteClick: () => void
  priceSummaryLines?: ProductReceiptLine[]
  lineTotal?: number
  isEditMode?: boolean
  productUnitPrice?: number
  onProductUnitPriceChange?: (value: number) => void
  onDiscountChange?: (value: number) => void
  onDeleteSubItem?: (subItemId: number) => void
  onEditDecoration?: (subItemId: number) => void
  onChangeDecoration?: (subItemId: number) => void
  onAddDecoration?: () => void
  onAddCustomLine?: () => void
  canAddCustomLine?: boolean
  quantity: number
  onQuantityChange: (value: number) => void
  isReadOnly?: boolean
}) => {
  const { t } = useLocalization()
  const showPriceSummary =
    priceSummaryLines &&
    priceSummaryLines.length >= 1 &&
    lineTotal !== undefined

  if (showPriceSummary) {
    return (
      <TableCell className="py-4 pl-2 pr-4 text-xs text-gray-700 align-top max-sm:px-5 max-sm:pt-0">
        <Row className="w-full items-start justify-end gap-8 max-sm:!flex-col max-sm:gap-3">
          <Row className="items-center gap-2 max-sm:w-full max-sm:justify-between">
            <span className="hidden text-xs text-muted-foreground max-sm:inline">
              {t('Quantity', 'Antal')}
            </span>
            <ProductRowQuantityControl
              isReadOnly={isReadOnly}
              isEditMode={isEditMode}
              quantity={quantity}
              onQuantityChange={onQuantityChange}
              className="mt-0.5 max-sm:mt-0"
            />
          </Row>
          <div className="min-w-0 flex-1 max-sm:w-full">
            <ProductRowPriceSummary
              lines={priceSummaryLines}
              total={lineTotal}
              isEditMode={isEditMode}
              productUnitPrice={productUnitPrice}
              onProductUnitPriceChange={onProductUnitPriceChange}
              discount={discount}
              onDiscountChange={onDiscountChange}
              onDeleteSubItem={onDeleteSubItem}
              onEditDecoration={onEditDecoration}
              onChangeDecoration={onChangeDecoration}
              onAddDecoration={onAddDecoration}
              onAddCustomLine={onAddCustomLine}
              canAddCustomLine={canAddCustomLine}
            />
          </div>
          {showDelete ? (
            <button
              type="button"
              onClick={onDeleteClick}
              className="mt-0.5 shrink-0 rounded p-1 opacity-0 transition-opacity hover:bg-red-50 group-hover:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
            </button>
          ) : (
            <div className="w-5 shrink-0 max-sm:hidden" />
          )}
        </Row>
      </TableCell>
    )
  }

  return (
    <TableCell className="py-2 px-5 text-sm text-gray-700 align-middle text-right">
      <Row className="items-center justify-end gap-3">
        <ProductRowQuantityControl
          isReadOnly={isReadOnly}
          isEditMode={isEditMode}
          quantity={quantity}
          onQuantityChange={onQuantityChange}
        />
        {discount > 0 ? (
          <>
            <span className="line-through text-gray-500 text-xs">
              {subtotalBeforeDiscount.toLocaleString('sv-SE')}
            </span>
            <span className="text-red-600 ml-0.5">
              {total.toLocaleString('sv-SE')}
            </span>
          </>
        ) : (
          <span>{total.toLocaleString('sv-SE')}</span>
        )}

        {showDelete ? (
          <button
            type="button"
            onClick={onDeleteClick}
            className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 shrink-0"
          >
            <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
          </button>
        ) : (
          <div className="w-[21px] shrink-0" />
        )}
      </Row>
    </TableCell>
  )
}
